import { createContext, ReactNode, useContext, useMemo } from 'react';
import { useParams } from 'react-router';

import { useTQModelContext } from 'components/TQModels/context/TQModelContext';
import { PlanScenarioParams } from 'constants/routes';
import { CollaboratorRoleAccessType } from 'types';
import { SPMCollaborationRole } from 'types/SPM';

import { TQ_ACTIONS } from './Permissions/constants';
import { usePermissions } from './Permissions/Permissions';

export type SPMCollaborationContextType = {
  isCollaborationUser: boolean;
  collaborationRole: SPMCollaborationRole;
};

const SPMCollaborationContext = createContext<SPMCollaborationContextType>({} as SPMCollaborationContextType);
export const useSPMCollaborationContext = () => useContext(SPMCollaborationContext);

type Props = {
  children: ReactNode;
};

export const SPMCollaborationContextProvider = ({ children }: Props) => {
  const { hasAccess } = usePermissions();
  const { userPermissions } = useTQModelContext();
  const { planningCycleId } = useParams<PlanScenarioParams>();

  const hasRbacAccess = hasAccess({ actions: TQ_ACTIONS.TQ_READ });

  const isCollaborationUser = useMemo(() => {
    // This means they are SPM admins
    if (hasRbacAccess) {
      return false;
    }
    // Defaulting to seeing less if we're not sure what the user's permissions are.
    if (!userPermissions || !planningCycleId) {
      return true;
    }
    const allPermissionPaths = userPermissions.shared_resources.flatMap((sharedResource) => {
      return sharedResource.resource_path;
    });
    // This means they do not have rbac access but do have a share token to a dataobject
    return allPermissionPaths.includes(planningCycleId);
  }, [userPermissions, planningCycleId, hasRbacAccess]);

  const collaborationRole: SPMCollaborationRole = useMemo(() => {
    if (!isCollaborationUser || !userPermissions) {
      return null;
    }
    // Derive role from shared resource permissions when available.
    // For now, default to 'collaborator' as the base role.
    // Justin - i thought the default for SPM was edit mode? need to confirm with kam
    return CollaboratorRoleAccessType.COLLABORATOR;
  }, [isCollaborationUser, userPermissions]);

  const value = useMemo(() => ({ isCollaborationUser, collaborationRole }), [isCollaborationUser, collaborationRole]);

  return <SPMCollaborationContext.Provider value={value}>{children}</SPMCollaborationContext.Provider>;
};
